import "@/index.css";
import { TerminologyObserver } from "@/components/TerminologyObserver";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import StoreHome from "@/pages/StoreHome";
import StoreKpi from "@/pages/StoreKpi";
import StoreCockpit from "@/pages/StoreCockpit";
import { isStoreLevel, usesOrbitRoles } from "@/lib/roles";

function HygRedirect() { const { section } = useParams(); return <Navigate to={`/metier/hygiene/${section}`} replace />; }

function Home() {
  const { user } = useAuth();
  return isStoreLevel(user) && usesOrbitRoles(user?.tenant) ? <StoreHome /> : <Dashboard />;
}
import Activities from "@/pages/Activities";
import Issues from "@/pages/Issues";
import Plans from "@/pages/Plans";
import Missions from "@/pages/Missions";
import CentreOperations from "@/pages/ops/CentreOperations";
import SondagesTab from "@/pages/ops/SondagesTab";
import { InterventionsTab, PlansActionTab, AlertesTab } from "@/pages/ops/OpsWrappers";
import Intranet from "@/pages/Intranet";
import KPI from "@/pages/KPI";
import Notifications from "@/pages/Notifications";
import Admin from "@/pages/Admin";
import AdminForms from "@/pages/AdminForms";
import ImportSlotPage from "@/pages/admin/ImportSlotPage";
import InpulsePage from "@/pages/admin/inpulse/InpulsePage";
import BaseInfo from "@/pages/BaseInfo";
import InfoMag from "@/pages/InfoMag";
import Commerce from "@/pages/commerce/Commerce";
import ProjetDossier from "@/pages/commerce/projets/ProjetDossier";
import Qualite from "@/pages/qualite/Qualite";
import DocumentsHaccp from "@/pages/ressources/DocumentsHaccp";
import RessourcesRH from "@/pages/ressources/RessourcesRH";
import Elearning from "@/pages/elearning/Elearning";
import ElearningPlayer from "@/pages/elearning/Player";
import ElearningEditor from "@/pages/elearning/Editor";
import HaccpFormation from "@/pages/elearning/haccp/HaccpFormation";
import HaccpQcm from "@/pages/elearning/haccp/HaccpQcm";
import KpiCorner from "@/pages/KpiCorner";
import CornerRanking from "@/pages/CornerRanking";
import AlertesTerrain from "@/pages/AlertesTerrain";
import StoreActivite from "@/pages/StoreActivite";
import Profile from "@/pages/Profile";
import Stores from "@/pages/Stores";
import CalendarPage from "@/pages/CalendarPage";
import Lexique from "@/pages/Lexique";
import ReportStudio from "@/pages/reports/ReportStudio";
import CampaignsHub from "@/pages/campaigns/CampaignsHub";
import CampaignTracking from "@/pages/campaigns/CampaignTracking";
import StoreCampaigns from "@/pages/campaigns/StoreCampaigns";
import { SplashScreen } from "@/components/SplashScreen";
import Documents from "@/pages/Documents";
import StoreDetail from "@/pages/StoreDetail";
import RHHub from "@/pages/rh/RHHub";
import PlanningHub from "@/pages/planning/PlanningHub";
import MarketingHub from "@/pages/marketing/MarketingHub";
import MoyensGenerauxHub from "@/pages/mg/MoyensGenerauxHub";
import Metier from "@/pages/metier/Metier";
import SuperAdmin from "@/pages/SuperAdmin";
import { BrandProvider } from "@/lib/brand";
import ReleveKm from "@/pages/mg/ReleveKm";
import KpiBusiness from "@/pages/KpiBusinessV2";
import KpiCADaily from "@/pages/KpiCADailyV2";
import KpiRH from "@/pages/KpiRHV2";
import HsAnalyse from "@/pages/rh/HsAnalyse";
import KpiAchats from "@/pages/KpiAchats";
import KpiHygiene from "@/pages/KpiHygiene";
import KpiScores from "@/pages/KpiScores";
import KpiScoresRanking from "@/pages/KpiScoresRanking";
import KpiProduits from "@/pages/KpiProduits";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <div className="text-slate-500">Chargement…</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrandProvider>
      <BrowserRouter>
        <SplashScreen />
        <TerminologyObserver />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Protected><AppLayout /></Protected>}>
            <Route index element={<Home />} />
            <Route path="mes-kpis" element={<StoreKpi />} />
            <Route path="mon-cockpit" element={<Navigate to="/activite" replace />} />
            <Route path="activite" element={<StoreActivite />} />
            <Route path="mon-cockpit-legacy" element={<StoreCockpit />} />

            {/* ─── Centre des Opérations Terrain (hub) ─── */}
            <Route path="ops" element={<CentreOperations />}>
              <Route index element={<Navigate to="interventions" replace />} />
              <Route path="interventions" element={<InterventionsTab />} />
              <Route path="plans" element={<PlansActionTab />} />
              <Route path="alertes" element={<AlertesTab />} />
              <Route path="sondages" element={<SondagesTab />} />
            </Route>

            {/* Legacy URL → redirect to new hub */}
            <Route path="activities" element={<Navigate to="/ops/interventions" replace />} />
            <Route path="issues" element={<Navigate to="/ops/alertes" replace />} />
            <Route path="plans" element={<Navigate to="/ops/plans" replace />} />
            <Route path="missions" element={<Navigate to="/ops/interventions" replace />} />

            <Route path="journal" element={<Intranet />} />
            <Route path="kpi" element={<KPI />} />
            <Route path="kpi/business" element={<KpiBusiness />} />
            <Route path="kpi/ca-daily" element={<KpiCADaily />} />
            <Route path="kpi/rh" element={<KpiRH />} />
            <Route path="kpi/rh/hs" element={<HsAnalyse />} />
            <Route path="kpi/achats" element={<KpiAchats />} />
            <Route path="kpi/hygiene" element={<KpiHygiene />} />
            <Route path="kpi/scores" element={<KpiScoresRanking />} />
            <Route path="kpi/produits" element={<KpiProduits />} />
            <Route path="support/maintenance" element={<Navigate to="/metier/maintenance" replace />} />
            <Route path="metier/planning" element={<Protected roles={["admin", "director", "manager", "mm", "rt", "controller", "corner", "support"]}><PlanningHub /></Protected>} />
            <Route path="support/rh" element={<Protected roles={["admin", "director", "manager", "support", "controller"]}><RHHub /></Protected>} />
            <Route path="support/rh/cockpit" element={<Navigate to="/support/rh?s=cockpit" replace />} />
            <Route path="support/rh/recrutement" element={<Navigate to="/support/rh?s=recrutement" replace />} />
            <Route path="support/rh/activite" element={<Navigate to="/support/rh?s=cockpit" replace />} />
            <Route path="support/rh/admin" element={<Navigate to="/support/rh?s=admin" replace />} />
            <Route path="mg/releve-km" element={<Protected><ReleveKm /></Protected>} />
            <Route path="mg/releve-km/:assetId" element={<Protected><ReleveKm /></Protected>} />
            <Route path="metier" element={<Metier />} />
            <Route path="metier/:tab" element={<Metier />} />
            <Route path="metier/hygiene/:section" element={<Metier />} />
            <Route path="hygiene" element={<Navigate to="/metier/hygiene/cockpit" replace />} />
            <Route path="super-admin" element={<SuperAdmin />} />
            <Route path="hygiene/:section" element={<HygRedirect />} />
            <Route path="support/moyens-generaux" element={<Protected roles={["admin", "director", "manager", "support", "controller"]}><MoyensGenerauxHub /></Protected>} />
            <Route path="support/qualite" element={<Protected roles={["admin", "director", "manager", "support", "controller"]}><Qualite /></Protected>} />
            <Route path="intranet/ressources/documents-haccp" element={<DocumentsHaccp />} />
            <Route path="intranet/ressources/rh" element={<Protected roles={["admin", "director", "manager", "support", "controller"]}><RessourcesRH /></Protected>} />
            <Route path="elearning" element={<Elearning />} />
            <Route path="elearning/play/:pid" element={<ElearningPlayer />} />
            <Route path="elearning/haccp/formation" element={<HaccpFormation />} />
            <Route path="elearning/haccp/qcm" element={<HaccpQcm />} />
            <Route path="elearning/edit/:mid" element={<Protected roles={["admin", "director", "manager", "support", "controller"]}><ElearningEditor /></Protected>} />
            <Route path="support/marketing" element={<Protected roles={["admin", "director", "manager", "support", "controller"]}><MarketingHub /></Protected>} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="info-mag" element={<Protected roles={["admin", "director", "manager", "controller", "rt", "mm", "support"]}><InfoMag /></Protected>} />
            <Route path="stores" element={<Stores />} />
            <Route path="stores/:id" element={<StoreDetail />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="lexique" element={<Lexique />} />
            <Route path="reports" element={<ReportStudio />} />
            <Route path="campaigns" element={<Protected roles={["admin", "director", "manager", "support", "controller"]}><CampaignsHub /></Protected>} />
            <Route path="campaigns/:id" element={<Protected roles={["admin", "director", "manager", "support", "controller"]}><CampaignTracking /></Protected>} />
            <Route path="mes-campagnes" element={<StoreCampaigns />} />
            <Route path="documents" element={<Documents />} />
            <Route path="profile" element={<Profile />} />
            <Route path="admin" element={<Protected roles={["admin"]}><Admin /></Protected>} />
            <Route path="admin/forms" element={<Protected roles={["admin"]}><AdminForms /></Protected>} />
            <Route path="admin/imports/:kind" element={<Protected roles={["admin", "controller"]}><ImportSlotPage /></Protected>} />
            <Route path="admin/integrations/inpulse" element={<Protected roles={["admin"]}><InpulsePage /></Protected>} />
            <Route path="base-info" element={<Protected roles={["admin", "director"]}><BaseInfo /></Protected>} />
            <Route path="dev-plus" element={<Navigate to="/commerce" replace />} />
            <Route path="commerce" element={<Protected roles={["admin", "director", "manager", "controller", "rt", "mm", "support"]}><Commerce /></Protected>} />
            <Route path="commerce/projets/:pid" element={<Protected roles={["admin", "director", "manager", "controller", "rt", "mm", "support"]}><ProjetDossier /></Protected>} />
            <Route path="kpi-corner" element={<Protected><KpiCorner /></Protected>} />
            <Route path="alertes-terrain" element={<Navigate to="/activite?tab=alertes" replace />} />
            <Route path="classement" element={<Protected><CornerRanking /></Protected>} />
          </Route>
          {/* Catch-all → redirect any unknown route (incl. removed /kpi/rentabilite) back home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </BrandProvider>
    </AuthProvider>
  );
}

export default App;
